import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { BRAND, PRODUCT_GROUPS } from "@/data/content";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About Us" },
  { to: "/products", label: "Products" },
  { to: "/brands", label: "Brands" },
  { to: "/gallery", label: "Gallery" },
  { to: "/contact", label: "Contact Us" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-[100] transition-colors duration-500 ${
        scrolled ? "bg-[#050505]/80 backdrop-blur-xl border-b border-white/5" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto max-w-[1400px] px-5 md:px-10 h-20 md:h-24 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group">
          <img src={BRAND.logo} alt="Vernika Designs" className="h-10 w-10 md:h-11 md:w-11 rounded-full object-cover ring-1 ring-[#e7c15a]/40" />
          <div className="leading-none">
            <div className="font-serif-lux text-lg md:text-xl tracking-[0.12em] text-gold-gradient">VERNIKA DESIGNS</div>
            <div className="text-[9px] md:text-[10px] uppercase tracking-[0.28em] text-[#b8b8b8] mt-1">{BRAND.tagline}</div>
          </div>
        </Link>

        <div className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}-link`}
              className={({ isActive }) =>
                `relative text-sm tracking-wide transition-colors duration-300 ${
                  isActive ? "text-[#e7c15a]" : "text-[#e8e8e8] hover:text-[#e7c15a]"
                }`
              }
            >
              {({ isActive }) => (
                <span className="relative">
                  {l.label}
                  {isActive && (
                    <motion.span layoutId="nav-underline" className="absolute -bottom-2 left-0 h-px w-full bg-[#e7c15a]" />
                  )}
                </span>
              )}
            </NavLink>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-4">
          <a
            href={`tel:${BRAND.phoneRaw[0]}`}
            data-testid="nav-call-link"
            className="flex items-center gap-2 text-sm text-[#e8e8e8] hover:text-[#e7c15a] transition-colors"
          >
            <Phone size={15} strokeWidth={1.5} /> {BRAND.phones[0].replace("+91 ", "")}
          </a>
          <Link
            to="/contact?quote=1"
            data-testid="nav-quote-btn"
            className="group relative overflow-hidden border border-[#e7c15a] px-6 py-3 text-xs uppercase tracking-[0.2em] text-[#e7c15a]"
          >
            <span className="relative z-10 transition-colors duration-300 group-hover:text-[#050505]">Get a Quote</span>
            <span className="absolute inset-0 -translate-x-full bg-[#e7c15a] transition-transform duration-300 group-hover:translate-x-0" />
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden text-[#e7c15a] p-2"
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-[#050505] border-b border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                  className={({ isActive }) =>
                    `py-3 text-lg font-serif-lux tracking-wide border-b border-white/5 ${
                      isActive ? "text-[#e7c15a]" : "text-[#e8e8e8]"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <div className="flex gap-3 mt-5">
                <a href={`tel:${BRAND.phoneRaw[0]}`} className="flex-1 text-center border border-white/15 py-3 text-sm text-[#e8e8e8]">Call Now</a>
                <Link to="/contact?quote=1" className="flex-1 text-center bg-[#e7c15a] text-[#050505] py-3 text-sm font-medium">Get a Quote</Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
